import type { Message } from '@/shared/api';

import {
  OutgoingStatus,
  type OutgoingMessage,
  type OutgoingQueueStorage,
  type OutgoingQueueTransition,
} from './types';

type SendOutgoingMessage = (message: OutgoingMessage) => Promise<Message>;

const createOutgoingQueue = (
  senderId: string,
  storage: OutgoingQueueStorage,
  sendOutgoingMessage: SendOutgoingMessage,
) => {
  const messagesByClientId = new Map<string, OutgoingMessage>();
  const listeners = new Set<(transition: OutgoingQueueTransition) => void>();
  const inFlight = new Map<string, Promise<Message | null>>();
  let hydration: Promise<OutgoingMessage[]> | null = null;

  const snapshot = (): OutgoingMessage[] =>
    [...messagesByClientId.values()].filter((message) => message.senderId === senderId);

  const notify = (transition: OutgoingQueueTransition): void => {
    listeners.forEach((listener) => listener(transition));
  };

  const replace = async (
    previous: OutgoingMessage | null,
    current: OutgoingMessage,
    serverMessage?: Message,
  ): Promise<void> => {
    messagesByClientId.set(current.clientMessageId, current);
    await storage.save(current);
    notify({ current, previous, serverMessage });
  };

  const confirm = async (serverMessage: Message): Promise<void> => {
    const clientMessageId: string | null | undefined = serverMessage.clientMessageId;
    if (!clientMessageId) {
      return;
    }

    const current = messagesByClientId.get(clientMessageId);
    if (!current || current.senderId !== senderId) {
      return;
    }

    messagesByClientId.delete(clientMessageId);
    await storage.remove(clientMessageId);
    notify({ current, previous: current, serverMessage });
  };

  const send = (message: OutgoingMessage): Promise<Message | null> => {
    const existing = inFlight.get(message.clientMessageId);
    if (existing) {
      return existing;
    }

    const operation = (async (): Promise<Message | null> => {
      const current = messagesByClientId.get(message.clientMessageId);
      if (!current || current.senderId !== senderId) {
        return null;
      }

      const sending: OutgoingMessage = { ...current, status: OutgoingStatus.SENDING };
      await replace(current, sending);
      try {
        const serverMessage = await sendOutgoingMessage(sending);
        await confirm({
          ...serverMessage,
          clientMessageId: serverMessage.clientMessageId ?? sending.clientMessageId,
        });
        return serverMessage;
      } catch {
        const failed: OutgoingMessage = { ...sending, status: OutgoingStatus.FAILED };
        await replace(sending, failed);
        return null;
      } finally {
        inFlight.delete(message.clientMessageId);
      }
    })();

    inFlight.set(message.clientMessageId, operation);
    return operation;
  };

  const hydrate = (): Promise<OutgoingMessage[]> => {
    if (hydration) {
      return hydration;
    }

    hydration = (async (): Promise<OutgoingMessage[]> => {
      const stored = await storage.load();
      for (const message of stored) {
        if (message.senderId !== senderId) {
          continue;
        }

        const restored: OutgoingMessage = {
          ...message,
          status:
            message.status === OutgoingStatus.SENDING ? OutgoingStatus.PENDING : message.status,
        };
        messagesByClientId.set(restored.clientMessageId, restored);
        if (restored.status !== message.status) {
          await storage.save(restored);
        }
        notify({
          current: restored,
          previous: message.status === restored.status ? null : message,
        });
      }
      return snapshot();
    })();

    return hydration;
  };

  const enqueue = async (
    message: Omit<OutgoingMessage, 'createdAt' | 'senderId' | 'status'>,
  ): Promise<OutgoingMessage> => {
    const pending: OutgoingMessage = {
      ...message,
      createdAt: new Date().toISOString(),
      senderId,
      status: OutgoingStatus.PENDING,
    };
    await replace(null, pending);
    return pending;
  };

  const retry = async (clientMessageId: string): Promise<Message | null> => {
    const message = messagesByClientId.get(clientMessageId);
    if (!message || message.status !== OutgoingStatus.FAILED) {
      return null;
    }

    return send(message);
  };

  const flush = async (): Promise<void> => {
    await Promise.all(
      snapshot()
        .filter((message) => message.status === OutgoingStatus.PENDING)
        .map(send),
    );
  };

  const discard = async (clientMessageId: string): Promise<void> => {
    const current = messagesByClientId.get(clientMessageId);
    if (!current || current.senderId !== senderId || inFlight.has(clientMessageId)) {
      return;
    }

    messagesByClientId.delete(clientMessageId);
    await storage.remove(clientMessageId);
  };

  const subscribe = (listener: (transition: OutgoingQueueTransition) => void): (() => void) => {
    listeners.add(listener);
    return (): void => {
      listeners.delete(listener);
    };
  };

  return { confirm, discard, enqueue, flush, hydrate, retry, send, snapshot, subscribe };
};

export { createOutgoingQueue };
